<template>
  <div
    v-if="is_initialized"
    class="flex flex-col gap-6"
  >
    <slot
      :is-visible="is_edit && !is_read_only"
      name="partner-form-header"
      :reset="setInitial"
      :submit="validate"
    />

    <section>
      <div class="mb-5 flex items-center gap-3">
        <TitleForm>
          {{ $t('contact.labels.general') }}
        </TitleForm>
        <ContactStatus
          v-if="partner.id"
          :contact="partner"
          :is_show_timestamp="true"
        />
      </div>
      <b-field grouped>
        <BField
          v-if="is_read_only"
          :label="$t('partner.attributes.title')"
          labelFor="title"
        >
          <input
            id="title"
            class="input is-static"
            readonly
            :value="$t(`constants.profile_titles.${partner.attributes.title}`)"
          />
        </BField>
        <SelectContactTitle
          v-if="!is_read_only"
          :is-expanded="false"
          :title_prop="partner.attributes.title"
          @change="is_edit = true"
          @input="(title) => (partner.attributes.title = title)"
        />
        <BField
          expanded
          :label="`${$t('partner.attributes.first_name')} *`"
          labelFor="first-name"
        >
          <div class="field">
            <input
              id="first-name"
              v-model.trim="v$.partner.attributes.first_name.$model"
              class="input"
              :class="{ 'is-danger': v$.partner.attributes.first_name.$error, 'is-static': is_read_only }"
              data-test="partner-first-name"
              :readonly="is_read_only"
              type="text"
              @keyup="is_edit = true"
            />
            <div v-if="v$.partner.attributes.first_name.$error">
              <p
                v-if="v$.partner.attributes.first_name.required.$invalid"
                class="help is-danger"
              >
                {{ $t('partner.validation.needFirstName') }}
              </p>
            </div>
          </div>
        </BField>
        <BField
          expanded
          :label="`${$t('partner.attributes.last_name')} *`"
          labelFor="last-name"
        >
          <div class="field">
            <input
              id="last-name"
              v-model.trim="v$.partner.attributes.last_name.$model"
              class="input"
              :class="{ 'is-danger': v$.partner.attributes.last_name.$error, 'is-static': is_read_only }"
              data-test="partner-last-name"
              :readonly="is_read_only"
              type="text"
              @keyup="is_edit = true"
            />
            <div v-if="v$.partner.attributes.last_name.$error">
              <p
                v-if="v$.partner.attributes.last_name.required.$invalid"
                class="help is-danger"
              >
                {{ $t('partner.validation.needLastName') }}
              </p>
            </div>
          </div>
        </BField>
      </b-field>
      <BField
        :label="`${$t('partner.attributes.email_address')} *`"
        labelFor="email-address"
      >
        <div class="field">
          <div v-if="props.action === 'create'">
            <input
              id="email-address"
              class="input"
              :class="{ 'is-danger': v$.partner.attributes.email_address.$error }"
              data-test="partner-email-address"
              @input="debouncedEmailHandler"
            />
            <div v-if="v$.partner.attributes.email_address.$error">
              <p
                v-if="v$.partner.attributes.email_address.required.$invalid"
                class="help is-danger"
              >
                {{ $t('partner.validation.requireEmailAddress') }}
              </p>
              <p
                v-if="v$.partner.attributes.email_address.email.$invalid"
                class="help is-danger"
              >
                {{ $t('partner.validation.mustBeEmailAddress') }}
              </p>
              <p
                v-if="v$.partner.attributes.email_address.emailAddressUniq.$invalid"
                class="help is-danger"
              >
                {{ $t('validation.emailTaken') }}
              </p>
              <p
                v-if="v$.partner.attributes.email_address.emailAddressDemo.$invalid"
                class="help is-danger"
              >
                {{ $t('validation.emailAddressDemo', { email_address_suffix }) }}
              </p>
            </div>
          </div>
          <div
            v-else
            class="field is-inline-flex items-center"
          >
            <div
              class="truncate"
              data-test="partner-email-address-read-only"
            >
              {{ partner.attributes.email_address }}
            </div>
            <div v-if="!is_read_only && !is_userless">
              <a
                v-if="partner.attributes.profiles_count === 1"
                class="icon is-clickable"
                @click="openUpdateEmailAddressModal"
              >
                <FontAwesomeIcon :icon="['far', 'pen']" />
              </a>
              <a
                v-else
                v-tooltip="$t('profile.labels.multipleProfiles')"
                class="icon flex-center is-clickable"
              >
                <FontAwesomeIcon icon="circle-info" />
              </a>
            </div>
          </div>
        </div>
      </BField>
      <EmailAddressStatusIndicator :profile="partner" />
      <b-field grouped>
        <b-field
          expanded
          :label="$t('partner.attributes.phone_mobile')"
          labelFor="phone_mobile"
        >
          <input
            id="phone_mobile"
            v-model.trim="partner.attributes.phone_mobile"
            class="input"
            :class="{ 'is-static': is_read_only }"
            :readonly="is_read_only"
            @keyup="is_edit = true"
          />
        </b-field>

        <b-field
          expanded
          :label="$t('partner.attributes.phone_landline')"
          labelFor="phone_landline"
        >
          <input
            id="phone_landline"
            v-model.trim="partner.attributes.phone_landline"
            class="input"
            :class="{ 'is-static': is_read_only }"
            :readonly="is_read_only"
            @keyup="is_edit = true"
          />
        </b-field>

        <BField
          expanded
          :label="$t('partner.attributes.profession')"
          labelFor="profession"
        >
          <input
            id="profession"
            v-model.trim="partner.attributes.profession"
            class="input"
            :class="{ 'is-static': is_read_only }"
            :readonly="is_read_only"
            @keyup="is_edit = true"
          />
        </BField>
      </b-field>

      <BField :label="$t('partner.attributes.language')">
        <SelectLanguage
          :is_read_only="is_read_only"
          :language_prop="partner.attributes.language"
          @change="is_edit = true"
          @input="(language) => (partner.attributes.language = language)"
        />
      </BField>
    </section>

    <section v-if="props.action !== 'create'">
      <div class="mb-5 flex items-center justify-between">
        <TitleForm>
          {{ $t('company.labels.company') }}
        </TitleForm>
        <ButtonTertiary
          data-test="partner-change-company"
          @onClick="show_modal_select_company = true"
        >
          {{ $t('company.actions.changeCompany') }}
        </ButtonTertiary>
      </div>
      <BField
        :label="`${$t('company.attributes.name')} *`"
        labelFor="name"
      >
        <div class="field">
          <input
            id="name"
            v-model.trim="v$.company.attributes.name.$model"
            class="input"
            :class="{ 'is-danger': v$.company.attributes.name.$error }"
            data-test="company-name"
            type="text"
            @keyup="is_edit = true"
          />
          <div v-if="v$.company.attributes.name.$error">
            <p
              v-if="v$.company.attributes.name.required.$invalid"
              class="help is-danger"
            >
              {{ $t('validation.needName') }}
            </p>
            <p
              v-if="v$.company.attributes.name.companyNameUniq.$invalid"
              class="help is-danger"
              data-test="company-name-name-taken"
            >
              {{ $t('validation.nameAlreadyTaken') }}
            </p>
          </div>
        </div>
      </BField>
      <AddressForm
        v-model:address_city="company.attributes.address_city"
        v-model:address_country="company.attributes.address_country"
        v-model:address_number="company.attributes.address_number"
        v-model:address_street="company.attributes.address_street"
        v-model:address_zip="company.attributes.address_zip"
        class="field"
        prefix="company"
        @edit="is_edit = true"
      />
    </section>

    <section v-if="props.action === 'create'">
      <div>
        <b-radio
          data-test="partner-add-company"
          :modelValue="is_add_company"
          :native-value="false"
          @update:modelValue="[(is_add_company = false), (company = null), v$.$reset()]"
        >
          {{ $t('partner.labels.addExistingCompany') }}
        </b-radio>
        <b-radio
          data-test="partner-new-company"
          :modelValue="is_add_company"
          :native-value="true"
          @update:modelValue="[(is_add_company = true), v$.$reset(), (company = { attributes: { name: '' } })]"
        >
          {{ $t('partner.labels.createNewCompany') }}
        </b-radio>
      </div>

      <div v-if="is_add_company === false && company === null">
        <label
          class="label"
          for="select-company"
        >
          {{ $t('company.actions.searchCompany') }}
        </label>
        <SelectCompany
          id="select-company"
          :companies="companies"
          @selectCompany="(val) => (company = val)"
        />
      </div>

      <div
        v-if="company && is_add_company === false"
        class="mt-4 mb-4 flex items-center"
      >
        <div class="font-medium">{{ $t('partner.labels.selectedCompany') }}:</div>
        <div class="ml-2">
          {{ company.attributes.name }}
        </div>
        <ButtonTertiary
          class="ml-4"
          @onClick="company = null"
        >
          {{ $t('button.actions.reset') }}
        </ButtonTertiary>
      </div>

      <div v-if="v$.company.attributes.name.$error">
        <p
          v-if="v$.company.attributes.name.required.$invalid"
          class="help is-danger"
        >
          {{ $t('validation.needCompany') }}
        </p>
      </div>

      <section v-if="is_add_company">
        <BField
          :label="`${$t('company.attributes.name')} *`"
          labelFor="name"
        >
          <div class="field">
            <input
              id="name"
              v-model.trim="v$.company.attributes.name.$model"
              class="input"
              :class="{ 'is-danger': v$.company.attributes.name.$error }"
              data-test="company-name"
              type="text"
              @keyup="is_edit = true"
            />
            <div v-if="v$.company.attributes.name.$error">
              <p
                v-if="v$.company.attributes.name.required.$invalid"
                class="help is-danger"
              >
                {{ $t('validation.needName') }}
              </p>
              <p
                v-if="v$.company.attributes.name.companyNameUniq.$invalid"
                class="help is-danger"
              >
                {{ $t('validation.nameAlreadyTaken') }}
              </p>
            </div>
          </div>
        </BField>

        <TitleForm class="mt-4 mb-4">
          {{ $t('contact.labels.address') }}
        </TitleForm>
        <AddressForm
          v-model:address_city="company.attributes.address_city"
          v-model:address_country="company.attributes.address_country"
          v-model:address_number="company.attributes.address_number"
          v-model:address_street="company.attributes.address_street"
          v-model:address_zip="company.attributes.address_zip"
          class="field"
          prefix="company"
          @edit="is_edit = true"
        />
      </section>
    </section>

    <section>
      <TitleForm class="mb-4">
        {{ $t('partner.attributes.internal_note') }}
      </TitleForm>
      <EditorZiggu
        v-model:content_html="partner.attributes.internal_note_html"
        :content_key="partner.id"
        data-test="partner-internal-note"
        :is_textarea="true"
        @keyup="is_edit = true"
      />
    </section>

    <footer>
      <slot
        :is-visible="is_edit"
        name="partner-form-footer"
        :reset="setInitial"
        :submit="validate"
      />
    </footer>

    <PrimeDialog
      :visible="cannot_update_field_modal"
      modal
      :header="$t('profile.labels.changeEmailAddress', { profile_name: partner.attributes.name })"
      @update:visible="cannot_update_field_modal = false"
    >
      <p>{{ $t('profile.labels.maintainerCannotUpdateEmailAddress') }}</p>
      <template #footer>
        <ButtonPrimary @onClick="cannot_update_field_modal = false">
          {{ $t('modal.labels.gotItMessage') }}
        </ButtonPrimary>
      </template>
    </PrimeDialog>

    <PrimeDialog
      :visible="show_update_email_modal"
      modal
      :header="$t('profile.labels.changeEmailAddress', { profile_name: partner.attributes.name })"
      @update:visible="show_update_email_modal = false"
    >
      <UpdateEmailAddressModal
        ref="update_email_modal_ref"
        :profile="partner"
        :email_address_old="partner.attributes.email_address"
        @close="show_update_email_modal = false"
        @updateEmailAddress="updateEmailAddress"
      />
      <template #footer>
        <div class="flex justify-end gap-2">
          <ButtonTertiary @onClick="show_update_email_modal = false">
            {{ $t('modal.actions.cancel') }}
          </ButtonTertiary>
          <ButtonPrimary @onClick="update_email_modal_ref.submit()">
            {{ $t('profile.actions.changeEmail') }}
          </ButtonPrimary>
        </div>
      </template>
    </PrimeDialog>

    <PrimeDialog
      :visible="show_modal_select_company"
      modal
      :header="$t('company.actions.changeCompany')"
      @update:visible="cancelChangePartnerCompany"
    >
      <div class="flex flex-col gap-2">
        <label
          class="text-sm font-medium text-gray-700"
          for="select-company"
        >
          {{ $t('company.actions.searchCompany') }}
        </label>
        <PSelect
          v-model="partner.attributes.company_id"
          data-test="select-company"
          :options="company_options"
          optionLabel="name"
          optionValue="id"
          class="w-full"
        />
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <ButtonTertiary @onClick="cancelChangePartnerCompany">
            {{ $t('modal.actions.cancel') }}
          </ButtonTertiary>
          <ButtonPrimary
            data-test="modal-confirm-primary"
            @onClick="changePartnerCompany"
          >
            {{ $t('modal.actions.submit') }}
          </ButtonPrimary>
        </div>
      </template>
    </PrimeDialog>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { helpers, required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import EmailAddressStatusIndicator from '@/app/_employees/components/users/EmailAddressStatusIndicator.vue';
import AddressForm from '@/app/shared_components/geo/AddressForm.vue';
import SelectContactTitle from '@/app/shared_components/selects/SelectContactTitle.vue';
import SelectCompany from '@/app/shared_components/selects/SelectCompany.vue';
import UpdateEmailAddressModal from '@/app/_employees/components/contacts/TheUpdateEmailAddressModal.vue';
import { Employee, ProfileRecord } from '@/app/data/model_constants';
import { email } from '@/app/util/validators';
import EditorZiggu from '@/app/shared_components/EditorZiggu.vue';
import useContactEmail from '@/app/util/use_contact_email';
import useUserlessProfile from '@/composables/useUserlessProfile';
import SelectLanguage from '@/app/shared_components/selects/SelectLanguage.vue';
import ContactStatus from '@/app/_employees/components/contacts/ContactStatus.vue';

defineOptions({ name: 'PartnerForm' });

const { withAsync } = helpers;

const props = defineProps({
  partner_prop: { type: Object, required: true },
  company_prop: { type: Object, default: null },
  action: {
    type: String,
    validator: (val) => ['create', 'edit'].includes(val),
    required: true,
  },
});

const emit = defineEmits(['updatePartner', 'updateCompany', 'setPartner']);

const store = useStore();
const { email_address_suffix, emailAddressDemo, isEmailUnique, createDebouncedEmailHandler } = useContactEmail();

const partner = ref(null);
const { is_userless } = useUserlessProfile(partner);
const company = ref({ attributes: { name: '' } });
const is_add_company = ref(null);
const show_modal_select_company = ref(false);
const is_edit = ref(false);
const is_initialized = ref(false);
const cannot_update_field_modal = ref(false);
const show_update_email_modal = ref(false);
const update_email_modal_ref = ref(null);

const current_client = computed(() => store.getters.current_client);
const current_profile = computed(() => store.getters.current_profile);
const companies = computed(() => store.getters.companies);
const company_options = computed(() => _.map(companies.value, (c) => ({ id: c.id, name: c.attributes.name })));

const existing_company_names = computed(
  () =>
    new Set(
      _.chain(companies.value)
        .reject(['id', _.get(props.partner_prop, 'attributes.company_id')])
        .map('attributes.name')
        .value(),
    ),
);

const is_read_only = computed(() => {
  const non_editable = [ProfileRecord.STATUS_ACTIVE, ProfileRecord.STATUS_BLOCKED];
  return props.action === 'edit' && _.includes(non_editable, partner.value.attributes.status);
});

const rules = computed(() => ({
  partner: {
    attributes: {
      first_name: { required },
      last_name: { required },
      email_address: {
        required,
        email,
        emailAddressUniq: withAsync((email_address) => {
          if (props.action === 'edit' || _.isNil(email_address) || email_address === '') return Promise.resolve(true);
          return isEmailUnique(email_address.trim());
        }),
        emailAddressDemo,
      },
    },
  },
  company: {
    attributes: {
      name: {
        required,
        companyNameUniq(val) {
          if (is_add_company.value || props.action === 'edit') return !existing_company_names.value.has(val);
          return true;
        },
      },
    },
  },
}));

const v$ = useVuelidate(rules, { partner, company });

async function setInitial() {
  try {
    await store.dispatch('FETCH_COMPANIES');
  } catch {
    // continue initializing even if fetch fails
  }
  partner.value = _.cloneDeep(props.partner_prop);
  company.value = _.cloneDeep(props.company_prop) || { attributes: { name: '' } };
  is_edit.value = false;
  is_initialized.value = true;
}

watch(
  () => props.partner_prop,
  (new_val, old_val) => {
    if (new_val && old_val && new_val.id !== old_val.id) setInitial();
  },
);
watch(
  () => props.company_prop,
  (new_val, old_val) => {
    if (new_val && old_val && new_val.id !== old_val.id) setInitial();
  },
);
setInitial();

function openUpdateEmailAddressModal() {
  if (current_profile.value.attributes.role !== Employee.ROLE_OWNER) {
    cannot_update_field_modal.value = true;
    return;
  }
  show_update_email_modal.value = true;
}

function updateEmailAddress(email_address_new) {
  partner.value.attributes.email_address = email_address_new;
  emit('updatePartner', partner.value);
  is_edit.value = false;
}

const debouncedEmailHandler = createDebouncedEmailHandler(() => v$.value.partner.attributes, partner);

function validate() {
  v$.value.$touch();
  if (v$.value.$invalid) return;
  if (props.action === 'edit') {
    emit('updatePartner', partner.value);
    emit('updateCompany', company.value);
    is_edit.value = false;
  } else if (props.action === 'create') {
    if ('id' in company.value) partner.value.attributes.company_id = company.value.id;
    emit('setPartner', partner.value, company.value);
  }
}

defineExpose({ validate });

function changePartnerCompany() {
  const company_selected = _.find(companies.value, ['id', partner.value.attributes.company_id]);
  if (company_selected) company.value = _.cloneDeep(company_selected);
  emit('updatePartner', partner.value);
  is_edit.value = false;
  show_modal_select_company.value = false;
}

function cancelChangePartnerCompany() {
  partner.value.attributes.company_id = props.partner_prop.attributes.company_id;
  show_modal_select_company.value = false;
}
</script>
